import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, useT, withLocale } from '../i18n';
import { api, ApiRequestError } from '../api/client';

type Track = 'enterprise' | 'government';

// The spec names the eight/ten domain sets and the six-level maturity
// model but does not publish an authored question bank — one canonical
// Likert question per domain (reusing the site's existing
// assess.question/assess.option.0-4 copy) is this implementation's own
// documented choice, matching backend/src/assessment/engine.ts's comment
// to the same effect. Domain order/ids are kept in exact sync with
// ENTERPRISE_DOMAINS / GOVERNMENT_DOMAINS there.
const ENTERPRISE_DOMAINS = ['strategy', 'data', 'ai', 'governance', 'security', 'platform', 'talent', 'operations'];
const GOVERNMENT_DOMAINS = ['ministries', 'agencies', 'services', 'data', 'infrastructure', 'cybersecurity', 'ai', 'digital_identity', 'interoperability', 'digital_skills'];

function domainLabel(t: (k: string) => string, domain: string): string {
  const key = `assess.domain.${domain}`;
  const label = t(key);
  if (label && label !== key) return label;
  // government-track domains have no dedicated i18n copy sourced yet —
  // humanize the slug rather than leaving a raw i18n key on screen.
  return domain
    .split('_')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

type Report = {
  score: number;
  maturity_level: number;
  maturity_level_name: string;
  domain_breakdown: Array<{ domain: string; score: number }>;
  sector_comparison: { available: boolean; reason?: string; average_score?: number; sample_size?: number };
  roadmap_outline: Array<{ stage: string; horizon_months: string }>;
  recommended_cta: string;
};

export default function Assessment() {
  const locale = useLocale();
  const t = useT();
  const [track, setTrack] = useState<Track | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);

  const domains = track === 'government' ? GOVERNMENT_DOMAINS : ENTERPRISE_DOMAINS;

  async function start(chosenTrack: Track) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ data: { session_id: string } }>('/api/v1/assessment/sessions', { track: chosenTrack });
      setSessionId(res.data.session_id);
      setTrack(chosenTrack);
      setStep(0);
      setAnswers({});
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not start the assessment — please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function selectAnswer(domain: string, value: number) {
    setAnswers((prev) => ({ ...prev, [domain]: value }));
    if (!sessionId) return;
    // submitted incrementally per spec §22.11 ("batch or incremental
    // submission") — a visitor who abandons the assessment mid-way has
    // still had their answers so far recorded.
    try {
      await api.post(`/api/v1/assessment/sessions/${sessionId}/answers`, {
        answers: [{ domain, question_id: `${domain}_q1`, answer_value: value }],
      });
    } catch {
      // non-fatal — /complete re-validates all domains are answered and
      // this same answer is resubmitted there in the worst case.
    }
    if (step < domains.length - 1) {
      setStep(step + 1);
    } else {
      complete();
    }
  }

  async function complete() {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ data: Report }>(`/api/v1/assessment/sessions/${sessionId}/complete`, {});
      setReport(res.data);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not complete the assessment — please try again.');
    } finally {
      setBusy(false);
    }
  }

  function ctaCopy(cta: string): string {
    if (cta === 'low') return t('assess.cta_low');
    if (cta === 'mid') return t('assess.cta_mid');
    return t('assess.cta_high');
  }

  function roadmapCopy(score: number): string {
    if (score < 40) return t('assess.roadmap_low');
    if (score < 70) return t('assess.roadmap_mid');
    return t('assess.roadmap_high');
  }

  if (report) {
    return (
      <section className="section" style={{ paddingTop: 160 }}>
        <div className="container">
          <div className="section-head reveal is-visible">
            <span className="eyebrow">{t('assess.eyebrow')}</span>
            <h1>{t('assess.your_score')}: {Math.round(report.score)}</h1>
            <p className="body-lg">
              {t('assess.maturity_level')}: {report.maturity_level} — {report.maturity_level_name}
            </p>
          </div>

          <div className="card reveal is-visible" style={{ marginBottom: 24 }}>
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{t('assess.table_domain')}</th>
                  <th style={{ textAlign: 'right' }}>{t('assess.table_score')}</th>
                </tr>
              </thead>
              <tbody>
                {report.domain_breakdown.map((d) => (
                  <tr key={d.domain}>
                    <td>{domainLabel(t, d.domain)}</td>
                    <td style={{ textAlign: 'right' }}>{Math.round(d.score)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card reveal is-visible" style={{ marginBottom: 24 }}>
            <h3>{t('assess.roadmap_headline')}</h3>
            <p className="body-sm">{roadmapCopy(report.score)}</p>
            <ol>
              {report.roadmap_outline.map((r, i) => (
                <li key={i} className="body-sm">
                  {r.stage} — {r.horizon_months} months
                </li>
              ))}
            </ol>
          </div>

          <div className="card reveal is-visible" style={{ marginBottom: 24 }}>
            <h3>Sector comparison</h3>
            {report.sector_comparison.available ? (
              <p className="body-sm">
                Sector average: {Math.round(report.sector_comparison.average_score ?? 0)} (n={report.sector_comparison.sample_size})
              </p>
            ) : (
              <p className="body-sm">
                Not enough sourced sector data yet to show a real comparison ({report.sector_comparison.reason ?? 'insufficient_sourced_data'}) — this is shown
                honestly rather than as an invented benchmark.
              </p>
            )}
          </div>

          <Link className="btn btn-primary btn-lg" to={withLocale('/contact', locale) + `?purpose=request_assessment`}>
            {ctaCopy(report.recommended_cta)}
          </Link>
        </div>
      </section>
    );
  }

  if (track && sessionId) {
    const domain = domains[step];
    return (
      <section className="section" style={{ paddingTop: 160, minHeight: '60vh' }}>
        <div className="container">
          <button type="button" className="lisa-util-link" onClick={() => setTrack(null)} style={{ marginBottom: 16 }}>
            {t('assess.back')}
          </button>
          <p className="body-sm">
            {domain ? domainLabel(t, domain) : ''} — {step + 1} {t('assess.of')} {domains.length}
          </p>
          <h2>
            {t('assess.question')} {domain ? domainLabel(t, domain) : ''}
            {t('assess.question_suffix')}
          </h2>
          <div className="card-grid">
            {[0, 1, 2, 3, 4].map((v) => (
              <button
                key={v}
                type="button"
                className="card reveal is-visible"
                disabled={busy}
                style={{ textAlign: 'left', cursor: 'pointer', borderColor: answers[domain] === v ? 'var(--accent)' : undefined }}
                onClick={() => selectAnswer(domain, v)}
              >
                {t(`assess.option.${v}`)}
              </button>
            ))}
          </div>
          {error && (
            <p className="body-sm" style={{ color: 'var(--status-critical)', marginTop: 16 }}>
              {error}
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="section" style={{ paddingTop: 160 }}>
      <div className="container">
        <div className="section-head reveal is-visible">
          <span className="eyebrow">{t('assess.eyebrow')}</span>
          <h1>{t('assess.h1')}</h1>
          <p className="body-lg">{t('assess.lead')}</p>
        </div>
        <div className="card-grid">
          <button type="button" className="card reveal is-visible" disabled={busy} style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => start('enterprise')}>
            <h3>{t('assess.track_enterprise')}</h3>
            <p className="body-sm">{t('assess.track_enterprise_body')}</p>
          </button>
          <button type="button" className="card reveal is-visible" disabled={busy} style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => start('government')}>
            <h3>{t('assess.track_government')}</h3>
            <p className="body-sm">{t('assess.track_government_body')}</p>
          </button>
        </div>
        {error && (
          <p className="body-sm" style={{ color: 'var(--status-critical)', marginTop: 16 }}>
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
